import { useQueryState } from "nuqs";
import { api } from "~/trpc/react";
import { useState, useEffect } from "react";
import { callServerActionSafely } from "~/lib/client/call-server-action-safely";
import {
  useInitialDiscountCode,
  useIsInitialDiscountHydrated,
} from "~/components/providers/initial-discount-provider";
import {
  setDiscountCookie,
  clearDiscountCookie,
} from "~/server/actions/discount-cookies";

export const useDiscount = () => {
  const [code] = useQueryState("code");
  const initialDiscountCode = useInitialDiscountCode();
  const isInitialDiscountHydrated = useIsInitialDiscountHydrated();
  const [cookieCode, setCookieCode] = useState<string | null>(
    initialDiscountCode,
  );

  useEffect(() => {
    setCookieCode((currentCode) => currentCode ?? initialDiscountCode);
  }, [initialDiscountCode]);

  const activeCode = cookieCode;

  const query = api.discount.validate.useQuery(
    { code: activeCode || "" },
    {
      enabled:
        isInitialDiscountHydrated && !!activeCode && activeCode.length > 0,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const isValidDiscountCode = (codeToCheck: string | null): boolean => {
    if (!codeToCheck || codeToCheck.length === 0) return false;
    if (codeToCheck.length > 35) return false;
    return true;
  };

  const urlCodeQuery = api.discount.validate.useQuery(
    { code: code || "" },
    {
      enabled: isValidDiscountCode(code) && code !== cookieCode,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  useEffect(() => {
    if (urlCodeQuery.data && code && code !== cookieCode) {
      const validUntilIso = urlCodeQuery.data.validUntil.toISOString();

      const persistDiscountCode = async () => {
        const result = await callServerActionSafely(
          "set_discount_cookie",
          () => setDiscountCookie(code, validUntilIso),
          { discountCode: code },
        );

        if (result !== null) {
          setCookieCode(code);
        }
      };

      void persistDiscountCode();
    }
  }, [urlCodeQuery.data, code, cookieCode]);

  /**
   * Calculate the discounted price based on the original price
   * @param originalPrice - The original price in euros
   * @returns The discounted price
   */
  const calculateDiscountedPrice = (originalPrice: number): number => {
    if (!query.data?.discount || originalPrice <= 0) {
      return originalPrice;
    }

    const discountAmount = originalPrice * (query.data.discount / 100);
    const discountedPrice = originalPrice - discountAmount;

    return Math.max(0, Number(discountedPrice.toFixed(2)));
  };

  /**
   * Calculate the discount amount (how much is saved)
   * @param originalPrice - The original price in euros
   * @returns The amount saved due to discount
   */
  const getDiscountAmount = (originalPrice: number): number => {
    if (!query.data?.discount || originalPrice <= 0) {
      return 0;
    }

    const discountAmount = originalPrice * (query.data.discount / 100);
    return Number(discountAmount.toFixed(2));
  };

  /**
   * Get discount percentage as a formatted string
   * @returns Formatted percentage string (e.g., "50%")
   */
  const getDiscountPercentageString = (): string => {
    if (!query.data?.discount) return "0%";
    return `${query.data.discount}%`;
  };

  /**
   * Check if the current discount is a free session (100% off)
   * @returns True if discount is 100%
   */
  const isFreeSession = (): boolean => {
    return query.data?.discount === 100;
  };

  /**
   * Clear the saved discount from cookies
   */
  const clearDiscount = async (): Promise<void> => {
    const result = await callServerActionSafely("clear_discount_cookie", () =>
      clearDiscountCookie(),
    );

    if (result !== null) {
      setCookieCode(null);
    }
  };

  return {
    ...query,
    discount: query.data,
    hasDiscount: !!query.data?.discount,
    discountPercentage: query.data?.discount || 0,
    discountCode: query.data?.code || activeCode,
    campaignName: query.data?.name,
    validUntil: query.data?.validUntil,
    activeCode,
    calculateDiscountedPrice,
    getDiscountAmount,
    getDiscountPercentageString,
    isFreeSession,
    clearDiscount,

    isValidating:
      !isInitialDiscountHydrated || query.isLoading || urlCodeQuery.isLoading,
    hasValidationError: query.isError,
    isProcessingUrlCode: urlCodeQuery.isLoading,
  };
};

export type UseDiscountReturn = ReturnType<typeof useDiscount>;
